/**
 * The search box in the header — it searches as you type.
 *
 * A pause in typing asks for the stock records that hold every word: counts,
 * transfer requests, transfers, sales reports, branches, employees, items,
 * the customer catalogue, price lists, production runs and folders. They
 * drop down under the box, a few of each; ↑/↓ move through them and Enter
 * opens the one chosen. Enter with nothing chosen, or the last line, opens
 * the full search page, where documents are searched too.
 *
 * Ctrl+K (and `/` when not typing) focuses the box; `/` is left to the
 * search page when already there.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRightLeft, BookMarked, ClipboardCheck, ClipboardList, Factory, FileSpreadsheet, FileText,
  FolderOpen, LoaderCircle, Package, Search, Store, Tag, Users,
} from 'lucide-react';
import { api } from './cabinet/shared';

const ICONS = {
  counts: ClipboardList, transfer_requests: ClipboardCheck, transfers: ArrowRightLeft,
  sales_reports: FileSpreadsheet, branches: Store, employees: Users, items: Package,
  catalogue: BookMarked, price_lists: Tag, production: Factory, folders: FolderOpen,
};
const WAIT_MS = 250;          // a pause in typing, not every key
const PER_KIND = 4;

export default function HeaderSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);       // { q, groups } for the words last asked
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(-1);     // the chosen line; the last is "see all"

  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const tag = (el?.tagName || '').toLowerCase();
      const typing = tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable;
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault(); inputRef.current?.focus();
      } else if (e.key === '/' && !typing && window.location.pathname !== '/search') {
        e.preventDefault(); inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // ask once typing pauses; a later word cancels the earlier answer
  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setData(null); setLoading(false); setActive(-1); return undefined; }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      api(`/api/v1/inventory/stocktake/search?q=${encodeURIComponent(q)}&per_kind=${PER_KIND}`)
        .then((r) => { if (!cancelled) { setData({ q, groups: r.groups || [] }); setActive(-1); } })
        // a company without the stock modules has no records — the full page still searches documents
        .catch(() => { if (!cancelled) { setData({ q, groups: [], failed: true }); setActive(-1); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, WAIT_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [term]);

  // a page opened, or a click outside, puts the list away
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  useEffect(() => {
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const lines = useMemo(() => (data?.groups || []).flatMap((g) => g.items.map((it) => ({ ...it, kind: g.kind }))), [data]);
  const q = term.trim();
  const showing = open && q.length >= 2;
  const seeAll = lines.length;                  // the index of the last line

  const go = (href) => { setOpen(false); setActive(-1); inputRef.current?.blur(); navigate(href); };
  const searchAll = () => go(`/search${q ? `?q=${encodeURIComponent(q)}` : ''}`);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault(); setOpen(true); setActive((a) => Math.min(seeAll, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setActive((a) => Math.max(-1, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (active >= 0 && active < lines.length) go(lines[active].href);
      else searchAll();
    } else if (e.key === 'Escape') {
      setOpen(false); setActive(-1);
    }
  };

  let n = -1;                                    // the running line number across the groups
  return (
    <div ref={boxRef} className="header-search hidden md:flex items-center relative flex-1 max-w-md mx-4">
      <Search size={15} className="absolute left-3 text-gray-400 pointer-events-none" />
      <input
        ref={inputRef}
        type="search"
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search counts, transfers, people, documents…  (Ctrl+K)"
        aria-label="Search records and documents"
        role="combobox"
        aria-expanded={showing}
        aria-controls="header-search-results"
        aria-autocomplete="list"
        aria-activedescendant={showing && active >= 0 ? `header-search-${active}` : undefined}
        className="w-full h-9 pl-9 pr-8 rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:bg-white focus:border-indigo-300"
      />
      {loading && <LoaderCircle size={14} className="absolute right-3 animate-spin text-gray-400" aria-hidden="true" />}

      {showing && (
        <div id="header-search-results" role="listbox" aria-label="Search results"
          className="absolute left-0 right-0 top-full mt-1 z-50 max-h-[70vh] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
          {!data && loading && <div className="px-3 py-3 text-xs text-gray-500">Searching…</div>}
          {data && !lines.length && !loading && (
            <div className="px-3 py-3 text-xs text-gray-500">
              {data.failed ? 'Stock records could not be searched just now.' : `No record holds every word of “${q}”.`}
              {' '}Press Enter to search documents too.
            </div>
          )}
          {(data?.groups || []).map((g) => {
            const Icon = ICONS[g.kind] || FileText;
            return (
              <div key={g.kind} className="py-1 border-b border-gray-100 last:border-b-0">
                <div className="flex items-center gap-1.5 px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  <Icon size={12} className="text-indigo-500" /> {g.label}{g.newest ? ' · newest' : ''}
                </div>
                {g.items.map((it) => {
                  n += 1;
                  const i = n;
                  return (
                    <button key={`${g.kind}-${it.id}`} id={`header-search-${i}`} type="button" role="option"
                      aria-selected={active === i}
                      onMouseEnter={() => setActive(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => go(it.href)}
                      className={'w-full text-left px-3 py-1.5 ' + (active === i ? 'bg-indigo-50' : 'hover:bg-gray-50')}>
                      <div className="text-sm text-gray-900 truncate">{it.title}</div>
                      {(it.subtitle || it.meta) && (
                        <div className="text-[11px] text-gray-500 truncate">{[it.subtitle, it.meta].filter(Boolean).join(' · ')}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
          <button id={`header-search-${seeAll}`} type="button" role="option" aria-selected={active === seeAll}
            onMouseEnter={() => setActive(seeAll)} onMouseDown={(e) => e.preventDefault()} onClick={searchAll}
            className={'w-full flex items-center gap-2 px-3 py-2 text-left text-xs border-t border-gray-100 '
              + (active === seeAll ? 'bg-indigo-50 text-indigo-800' : 'text-indigo-700 hover:bg-gray-50')}>
            <Search size={12} /> See all results for “{q}”, documents included
            <span className="ml-auto text-[10px] text-gray-400">Enter</span>
          </button>
        </div>
      )}
    </div>
  );
}
